import { HardHat, Plus, X } from "lucide-react";
import { useEffect, useState } from "react";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { IoIosSearch } from "react-icons/io";

import { ModalWrapper } from "../../components/AdminSideComponents/common/ModalWrapper";
import PageHeader from "../../components/AdminSideComponents/common/PageHeader";
import Pagination from "../../components/AdminSideComponents/common/Pagination";

import api from "../../configs/api";
import { confirmAction, showError, showSuccess } from "../../utils/toastUtils";

// ---- Row card -------------------------------------------------------

const MixRow = ({ mix, onEdit, onDelete, onToggleActive }) => (
  <tr className="border-b border-borderLight last:border-0">
    <td className="py-3 px-4 text-sm font-semibold text-headingText">
      {mix.grade}
    </td>
    <td className="py-3 px-4 text-sm text-descriptionText">{mix.type}</td>
    <td className="py-3 px-4 text-sm text-headingText font-medium">
      ₹{mix.price.toLocaleString("en-IN")}
    </td>
    <td className="py-3 px-4">
      <button
        onClick={() => onToggleActive(mix)}
        className={`text-xs px-2.5 py-1 rounded-full capitalize ${
          mix.isActive
            ? "bg-green-100 text-green-700"
            : "bg-gray-100 text-gray-500"
        }`}
      >
        {mix.isActive ? "Active" : "Inactive"}
      </button>
    </td>
    <td className="py-3 px-4">
      <div className="flex items-center gap-3">
        <button
          onClick={() => onEdit(mix)}
          className="text-primaryAccent hover:opacity-70"
          title="Edit"
        >
          <FiEdit2 size={15} />
        </button>
        <button
          onClick={() => onDelete(mix)}
          className="text-red-500 hover:text-red-600"
          title="Delete"
        >
          <FiTrash2 size={15} />
        </button>
      </div>
    </td>
  </tr>
);

// ---- Add Grade (grade + multiple types/prices at once) ----------------

const emptyTypeRow = () => ({ type: "", price: "" });

const AddGradeModal = ({ isOpen, onClose, onSaved }) => {
  const [grade, setGrade] = useState("");
  const [types, setTypes] = useState([emptyTypeRow(), emptyTypeRow(), emptyTypeRow()]);
  const [saving, setSaving] = useState(false);

  // Reset the form every time the modal is (re)opened
  useEffect(() => {
    if (isOpen) {
      setGrade("");
      setTypes([emptyTypeRow(), emptyTypeRow(), emptyTypeRow()]);
    }
  }, [isOpen]);

  const handleTypeChange = (index, field, value) => {
    setTypes((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const handleAddRow = () => {
    setTypes((prev) => [...prev, emptyTypeRow()]);
  };

  const handleRemoveRow = (index) => {
    setTypes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!grade.trim()) {
      showError("Grade name is required");
      return;
    }

    // Only keep rows where the admin actually typed a type name
    const filledRows = types.filter((row) => row.type.trim() !== "");

    if (filledRows.length === 0) {
      showError("Add at least one type with a price");
      return;
    }

    for (const row of filledRows) {
      if (row.price === "" || Number(row.price) < 0) {
        showError(`Enter a valid price for "${row.type}"`);
        return;
      }
    }

    const typeNames = filledRows.map((r) => r.type.trim().toLowerCase());
    if (new Set(typeNames).size !== typeNames.length) {
      showError("You've entered the same type name more than once");
      return;
    }

    try {
      setSaving(true);

      await api.post("/concrete-mix/bulk-create", {
        grade: grade.trim(),
        types: filledRows.map((row) => ({
          type: row.type.trim(),
          price: Number(row.price),
        })),
      });

      showSuccess(`${grade.trim().toUpperCase()} added successfully`);
      onSaved();
      onClose();
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to add grade");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalWrapper
      title="Add Grade"
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-lg"
      showFooter
      onPrimaryAction={handleSubmit}
      primaryActionLabel="Add Grade"
      primaryLoading={saving}
      primaryDisabled={saving}
    >
      <div className="space-y-5">
        {/* Grade name */}
        <div>
          <label className="block text-sm font-medium text-headingText mb-1">
            Grade Name
          </label>
          <input
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            placeholder="e.g., M20, M25, etc."
            className="w-full h-[42px] px-3 border border-borderLight outline-none focus:border-primaryAccent text-sm"
          />
        </div>

        {/* Types + prices */}
        <div>
          <label className="block text-sm font-medium text-headingText mb-2">
            Default Types
          </label>

          <div className="space-y-3">
            {types.map((row, index) => (
              <div key={index} className="flex items-center gap-3">
                <input
                  value={row.type}
                  onChange={(e) =>
                    handleTypeChange(index, "type", e.target.value)
                  }
                  placeholder="Type name"
                  className="flex-1 h-[42px] px-3 border border-borderLight outline-none focus:border-primaryAccent text-sm"
                />
                <input
                  type="number"
                  min="0"
                  value={row.price}
                  onChange={(e) =>
                    handleTypeChange(index, "price", e.target.value)
                  }
                  placeholder="0"
                  className="w-32 h-[42px] px-3 border border-borderLight outline-none focus:border-primaryAccent text-sm"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveRow(index)}
                  disabled={types.length === 1}
                  className="text-red-500 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Remove"
                >
                  <X size={18} />
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={handleAddRow}
            className="mt-3 text-sm font-medium text-primaryAccent hover:opacity-80"
          >
            + Add Type
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

// ---- Add / Edit form (single row — used for the pencil/edit icon) -----

const emptyForm = { grade: "", type: "", price: "", isActive: true };

const MixFormModal = ({ isOpen, onClose, initialData, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initialData?._id);

  useEffect(() => {
    if (initialData) {
      setForm({
        grade: initialData.grade || "",
        type: initialData.type || "",
        price: initialData.price ?? "",
        isActive: initialData.isActive ?? true,
      });
    } else {
      setForm(emptyForm);
    }
  }, [initialData, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    if (!form.grade.trim() || !form.type.trim() || form.price === "") {
      showError("Grade, type and price are all required");
      return;
    }

    if (Number(form.price) < 0) {
      showError("Price cannot be negative");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        grade: form.grade.trim(),
        type: form.type.trim(),
        price: Number(form.price),
        isActive: form.isActive,
      };

      if (isEdit) {
        await api.put(`/concrete-mix/${initialData._id}`, payload);
        showSuccess("Mix updated successfully");
      } else {
        await api.post("/concrete-mix", payload);
        showSuccess("Mix added successfully");
      }

      onSaved();
      onClose();
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to save mix");
    } finally {
      setSaving(false);
    }
  };

  // Submitting via Enter key inside the form fields
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <ModalWrapper
      title={isEdit ? "Edit Concrete Mix" : "Add Concrete Mix"}
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-md"
      showFooter
      onPrimaryAction={handleSubmit}
      primaryActionLabel={isEdit ? "Save Changes" : "Add Mix"}
      primaryLoading={saving}
      primaryDisabled={saving}
    >
      <div className="space-y-4" onKeyDown={handleKeyDown}>
        <div>
          <label className="block text-xs font-medium text-descriptionText mb-1">
            Grade (e.g. M20)
          </label>
          <input
            name="grade"
            value={form.grade}
            onChange={handleChange}
            placeholder="M20"
            className="w-full h-[42px] px-3 border border-borderLight outline-none focus:border-primaryAccent text-sm"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-descriptionText mb-1">
            Mix Type (e.g. Raftmix)
          </label>
          <input
            name="type"
            value={form.type}
            onChange={handleChange}
            placeholder="Raftmix"
            className="w-full h-[42px] px-3 border border-borderLight outline-none focus:border-primaryAccent text-sm"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-descriptionText mb-1">
            Price (₹ per unit)
          </label>
          <input
            type="number"
            min="0"
            name="price"
            value={form.price}
            onChange={handleChange}
            placeholder="3112"
            className="w-full h-[42px] px-3 border border-borderLight outline-none focus:border-primaryAccent text-sm"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-headingText">
          <input
            type="checkbox"
            name="isActive"
            checked={form.isActive}
            onChange={handleChange}
          />
          Active (visible on the public calculator)
        </label>
      </div>
    </ModalWrapper>
  );
};

// ---- Main page ---------------------------------------------------------

const AdminConcreteMix = () => {
  const [mixes, setMixes] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [editingMix, setEditingMix] = useState(null);

  const [gradeModalOpen, setGradeModalOpen] = useState(false);

  const limit = 10;

  const fetchMixes = async () => {
    try {
      setLoading(true);

      const res = await api.get(
        `/concrete-mix?page=${page}&limit=${limit}&search=${search}`
      );

      setMixes(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error(err);
      showError("Failed to load concrete mix data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMixes();
  }, [page, search]);

  const openEditModal = (mix) => {
    setEditingMix(mix);
    setFormOpen(true);
  };

  const handleToggleActive = async (mix) => {
    try {
      const res = await api.patch(`/concrete-mix/${mix._id}/toggle-active`);
      setMixes((prev) =>
        prev.map((m) => (m._id === mix._id ? res.data.data : m))
      );
      showSuccess(res.data.message);
    } catch (err) {
      showError("Failed to update status");
    }
  };

  const handleDelete = async (mix) => {
    const confirmed = await confirmAction({
      title: "Delete this mix?",
      text: `${mix.grade} - ${mix.type} will be permanently removed.`,
    });

    if (!confirmed) return;

    try {
      await api.delete(`/concrete-mix/${mix._id}`);
      setMixes((prev) => prev.filter((m) => m._id !== mix._id));
      showSuccess("Mix deleted successfully");
    } catch (err) {
      showError("Failed to delete mix");
    }
  };

  const total = pagination?.total ?? 0;

  return (
    <div className="p-6">
      <PageHeader
        title="Concrete Mix Pricing"
        breadcrumb="Dashboard / Concrete Mix Pricing"
        customSection={
          <span className="text-xs font-medium text-descriptionText border border-borderLight px-2.5 py-1 bg-surfaceColor">
            {total} entries
          </span>
        }
      />

      {/* SEARCH + ADD */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-descriptionText">
            <IoIosSearch className="size-4" />
          </span>

          <input
            type="text"
            placeholder="Search by grade or type..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="h-[42px] w-full sm:w-[25rem] pl-9 pr-4 border border-borderLight outline-none focus:border-primaryAccent text-sm"
          />
        </div>

        <button
          onClick={() => setGradeModalOpen(true)}
          className="flex items-center gap-2 h-[42px] px-4 bg-primaryAccent text-white text-sm font-medium hover:opacity-90"
        >
          <Plus size={16} /> Add Grade
        </button>
      </div>

      {/* TABLE */}
      <div className="border border-borderLight bg-surfaceColor overflow-x-auto">
        {loading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-4 bg-divider animate-pulse w-full" />
            ))}
          </div>
        ) : mixes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primaryAccent/10 text-primaryAccent mb-4">
              <HardHat size={28} />
            </div>
            <p className="text-lg font-semibold text-headingText">
              No concrete mix entries found
            </p>
          </div>
        ) : (
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-borderLight bg-bgLight text-left">
                <th className="py-3 px-4 text-xs font-semibold text-descriptionText uppercase">
                  Grade
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-descriptionText uppercase">
                  Type
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-descriptionText uppercase">
                  Price
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-descriptionText uppercase">
                  Status
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-descriptionText uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {mixes.map((mix) => (
                <MixRow
                  key={mix._id}
                  mix={mix}
                  onEdit={openEditModal}
                  onDelete={handleDelete}
                  onToggleActive={handleToggleActive}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!loading && (
        <Pagination pagination={pagination} page={page} onPageChange={setPage} />
      )}

      <AddGradeModal
        isOpen={gradeModalOpen}
        onClose={() => setGradeModalOpen(false)}
        onSaved={fetchMixes}
      />

      <MixFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        initialData={editingMix}
        onSaved={fetchMixes}
      />
    </div>
  );
};

export default AdminConcreteMix;